import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useUpdateSKUMutation } from "@/data/mutations";
import {
  SKU,
  SKUPageContent,
  SKUPageContentGenericTabIcon,
} from "@/data/types";
import {
  deleteTab,
  moveCMSTab,
  sanitizePageContent,
  updateTabDetails,
} from "@/lib/cms";

type CMSContextType = {
  sku: SKU;
  pageContent: SKUPageContent;
  activeTabIndex: number | undefined;
  onExpand: (tabIndex: number) => void;
  onMoveTabUp: (tabIndex: number) => void;
  onMoveTabDown: (tabIndex: number) => void;
  onCustomizeTab: (
    tabIndex: number,
    title: string | undefined,
    icon: SKUPageContentGenericTabIcon | undefined,
  ) => void;
  onExpandTab: (tabIndex: number) => void;
  onDeleteTab: (tabIndex: number) => void;
};

// TODO: This state should be moved to zustand to minimize re-renders
const CMSContext = createContext<CMSContextType | null>(null);

type Props = PropsWithChildren<{
  sku: SKU;
}>;

export const CMSProvider: FC<Props> = ({ sku, children }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>();
  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const pageContent = useMemo(() => {
    return sanitizePageContent(sku.page_content);
  }, [sku.page_content]);

  const onExpand = (tabIndex: number) => {
    setActiveTabIndex((prev) => (prev === tabIndex ? undefined : tabIndex));
  };

  const onMoveTabUp = useCallback(
    (tabIndex: number) => {
      updateSKUMutation.mutate({
        page_content: moveCMSTab(pageContent, tabIndex, tabIndex - 1),
      });
    },
    [pageContent, updateSKUMutation],
  );

  const onMoveTabDown = useCallback(
    (tabIndex: number) => {
      updateSKUMutation.mutate({
        page_content: moveCMSTab(pageContent, tabIndex, tabIndex + 1),
      });
    },
    [pageContent, updateSKUMutation],
  );

  const onCustomizeTab = useCallback(
    (
      tabIndex: number,
      title: string | undefined,
      icon: SKUPageContentGenericTabIcon | undefined,
    ) => {
      const payload = {
        tab_title: title,
        tab_icon: icon,
      };

      updateSKUMutation.mutate({
        page_content: updateTabDetails(pageContent, payload, tabIndex),
      });
    },
    [pageContent, updateSKUMutation],
  );

  const onExpandTab = useCallback((tabIndex: number) => {
    setActiveTabIndex((prev) => (prev === tabIndex ? undefined : tabIndex));
  }, []);

  const onDeleteTab = useCallback(
    (tabIndex: number) => {
      updateSKUMutation.mutate({
        page_content: deleteTab(pageContent, tabIndex),
      });
    },
    [pageContent, updateSKUMutation],
  );

  return (
    <CMSContext.Provider
      value={{
        sku,
        pageContent,
        activeTabIndex,
        onExpand,
        onMoveTabUp,
        onMoveTabDown,
        onCustomizeTab,
        onExpandTab,
        onDeleteTab,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCMSContext = () => {
  const context = useContext(CMSContext);

  if (!context) {
    throw new Error("useCMSContext must be used within a CMSProvider");
  }

  return context;
};
